import React from 'react';
import { useTranslation } from 'react-i18next';

import { QrCodeFieldConfig, QrFieldSource } from 'editor/src/store/design/types';

import { QrFieldDef } from 'editor/src/lib/qr';
import useTr from 'editor/src/util/useTr';

import styles from './index.module.scss';

const STATIC_OPTION = 'static';
const ELEMENT_OPTION_PREFIX = 'el:';
const COLUMN_OPTION_PREFIX = 'col:';

export interface QrTextField {
  uuid: number;
  name: string;
  /** Listed only while the field already reads it, and closed. */
  unavailable?: boolean;
}

interface Props {
  def: QrFieldDef;
  field: QrCodeFieldConfig | undefined;
  textFields: QrTextField[];
  /** Code-set columns on offer as a source; empty when the design is not
   *  bound, or the host does not offer them. */
  columns?: string[];
  onChange: (source: QrFieldSource | undefined) => void;
}

// A single field-mapping row in the QR configuration dialog: label + source picker
// (not included / a personalizable text element / static text) and, for static
// sources, the value input — a textarea, an options <select>, or a plain text input
// depending on the field definition.
function FieldSourceRow({ def, field, textFields, columns = [], onChange }: Props) {
  const { t } = useTranslation();
  const tr = useTr();
  const source = field?.source;

  // Fields with a fixed set of options (WiFi encryption/hidden) are always static —
  // there is nothing to link to a personalizable element, so the source select is
  // skipped entirely and the options select is bound straight to the static value.
  if (def.options) {
    const value = source?.type === 'static' ? source.value : def.options[0]?.value ?? '';
    return (
      <div className={styles.fieldRow}>
        <span className={styles.fieldLabel}>{t(def.label)}</span>
        <select
          className={`${styles.fieldSelect} cy-qr-field-${def.key}`}
          value={value}
          onChange={(e) => onChange({ type: 'static', value: e.target.value })}
        >
          {def.options.map((option) => (
            <option key={option.value} value={option.value}>
              {t(option.label)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // A column the dialog no longer offers stays visible while the field reads it,
  // so the seller sees what is in effect and can move the field off it.
  const currentColumn = source?.type === 'column' && !columns.includes(source.column) ? source.column : undefined;

  let selectValue = '';
  if (source?.type === 'static') {
    selectValue = STATIC_OPTION;
  } else if (source?.type === 'element') {
    selectValue = `${ELEMENT_OPTION_PREFIX}${source.elementId}`;
  } else if (source?.type === 'column') {
    selectValue = `${COLUMN_OPTION_PREFIX}${source.column}`;
  }

  function onSourceSelect(value: string) {
    if (value === STATIC_OPTION) {
      onChange({ type: 'static', value: source?.type === 'static' ? source.value : '' });
    } else if (value.startsWith(ELEMENT_OPTION_PREFIX)) {
      onChange({ type: 'element', elementId: Number(value.slice(ELEMENT_OPTION_PREFIX.length)) });
    } else if (value.startsWith(COLUMN_OPTION_PREFIX)) {
      onChange({ type: 'column', column: value.slice(COLUMN_OPTION_PREFIX.length) });
    } else {
      onChange(undefined);
    }
  }

  return (
    <div className={styles.fieldRow}>
      <span className={styles.fieldLabel}>{t(def.label)}</span>
      <select
        className={`${styles.fieldSelect} cy-qr-field-${def.key}`}
        value={selectValue}
        onChange={(e) => onSourceSelect(e.target.value)}
      >
        <option value="">{t('Not included')}</option>
        {textFields
          .filter((textField) => !textField.unavailable || selectValue === `${ELEMENT_OPTION_PREFIX}${textField.uuid}`)
          .map((textField) => (
            <option
              key={textField.uuid}
              value={`${ELEMENT_OPTION_PREFIX}${textField.uuid}`}
              disabled={textField.unavailable}
            >
              {textField.name}
            </option>
          ))}
        <option value={STATIC_OPTION}>{t('Static text')}</option>
        {(columns.length > 0 || currentColumn) && (
          <optgroup label={tr('editor-qr-field-source-column-group', 'Code set column (one value per printed unit)')}>
            {columns.map((column) => (
              <option key={column} value={`${COLUMN_OPTION_PREFIX}${column}`}>
                {column}
              </option>
            ))}
            {currentColumn && (
              <option value={`${COLUMN_OPTION_PREFIX}${currentColumn}`} disabled>
                {currentColumn}
              </option>
            )}
          </optgroup>
        )}
      </select>
      {source?.type === 'static' &&
        (def.multiline ? (
          <textarea
            className={`${styles.fieldStaticTextarea} cy-qr-static-${def.key}`}
            value={source.value}
            placeholder={t(def.label)}
            onChange={(e) => onChange({ type: 'static', value: e.target.value })}
          />
        ) : (
          <input
            className={`${styles.fieldStaticInput} cy-qr-static-${def.key}`}
            type="text"
            value={source.value}
            placeholder={t(def.label)}
            onChange={(e) => onChange({ type: 'static', value: e.target.value })}
          />
        ))}
    </div>
  );
}

export default React.memo(FieldSourceRow);
